import apiClient from '../utils/axios-config';
import { logger } from '../utils/logger';
import { TryOnImageProcessor } from '../utils/tryOnImageProcessor';
import enhancedAuthService from './enhanced-auth-service';

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev' || 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

/**
 * Interface for upload profile picture request
 */
export interface UploadProfilePictureRequest {
  image: string; // Base64 encoded image
}

/**
 * Interface for upload profile picture response
 */
export interface UploadProfilePictureResponse {
  profilePictureUrl: string;
  message: string;
}

/**
 * Interface for delete profile picture response
 */
export interface DeleteProfilePictureResponse {
  message: string;
}

/**
 * Interface for secure profile picture response
 */
export interface SecureProfilePictureResponse {
  signedUrl: string;
  expiresAt: string;
  message: string;
}

/**
 * Service for profile picture related API calls
 */
export class ProfilePictureService {
  /**
   * Upload a profile picture for try-on functionality
   * @param imageFile Original image file
   * @returns Upload response with profile picture URL
   */
  async uploadProfilePicture(imageFile: File): Promise<UploadProfilePictureResponse> {
    try {
      // Validate image for try-on use
      const validation = await TryOnImageProcessor.validateForTryOn(imageFile);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid image for try-on');
      }

      // Process image to exact try-on dimensions (320x640)
      logger.info('Processing image for try-on', { 
        fileName: imageFile.name, 
        originalSize: imageFile.size,
        targetDimensions: `${TryOnImageProcessor.TARGET_WIDTH}x${TryOnImageProcessor.TARGET_HEIGHT}`
      });

      const processingResult = await TryOnImageProcessor.processForTryOn(imageFile);
      
      logger.info('Image processed for try-on', {
        originalSize: processingResult.originalSize,
        processedSize: processingResult.processedSize,
        compressionRatio: processingResult.compressionRatio.toFixed(2),
        originalDimensions: processingResult.originalDimensions,
        processedDimensions: processingResult.processedDimensions,
      });

      // Convert processed file to base64
      const base64Image = await this.fileToBase64(processingResult.processedFile);
      
      const token = await enhancedAuthService.getAccessToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      const requestBody: UploadProfilePictureRequest = {
        image: base64Image,
      };

      const response = await apiClient.post<UploadProfilePictureResponse>(
        `${API_BASE_URL}/profile/picture`,
        requestBody,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      logger.info('Profile picture uploaded successfully', { 
        profilePictureUrl: response.data.profilePictureUrl,
        finalSize: processingResult.processedSize,
      });

      return response.data;
    } catch (error: any) {
      logger.error('Error uploading profile picture:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 400) {
        throw new Error(error.response.data?.message || 'Invalid image file');
      } else if (error.response?.status === 413) {
        throw new Error('Image file is too large. Maximum size is 10MB.');
      } else {
        throw new Error('Failed to upload profile picture. Please try again.');
      }
    }
  }

  /**
   * Delete the current profile picture
   * @returns Delete response
   */
  async deleteProfilePicture(): Promise<DeleteProfilePictureResponse> {
    try {
      const token = await enhancedAuthService.getAccessToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      logger.info('Deleting profile picture');

      const response = await apiClient.delete<DeleteProfilePictureResponse>(
        `${API_BASE_URL}/profile/picture`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      logger.info('Profile picture deleted successfully');

      return response.data;
    } catch (error: any) {
      logger.error('Error deleting profile picture:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        throw new Error('No profile picture found to delete.');
      } else {
        throw new Error('Failed to delete profile picture. Please try again.');
      }
    }
  }

  /**
   * Get secure profile picture URL
   * @returns Secure profile picture URL with expiration
   */
  async getSecureProfilePictureUrl(): Promise<SecureProfilePictureResponse> {
    try {
      const token = await enhancedAuthService.getAccessToken();
      if (!token) {
        throw new Error('Authentication token not found');
      }

      logger.info('Getting secure profile picture URL');

      const response = await apiClient.get<SecureProfilePictureResponse>(
        `${API_BASE_URL}/profile/picture/secure`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      logger.info('Secure profile picture URL retrieved successfully');

      return response.data;
    } catch (error: any) {
      logger.error('Error getting secure profile picture URL:', error);
      
      if (error.response?.status === 401) {
        throw new Error('Authentication failed. Please log in again.');
      } else if (error.response?.status === 404) {
        throw new Error('Profile picture not found.');
      } else {
        throw new Error('Failed to get profile picture URL. Please try again.');
      }
    }
  }

  /**
   * Convert file to base64 string
   * @param file File to convert
   * @returns Base64 string
   */
  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  }
}

export default new ProfilePictureService();